import { useCallback } from 'react'
import { getEnrollmentDraftEmail } from '../../../../utils/enrollmentDraftSelectors'
import type { AddressParts, ApplicationFormState, FieldErrorKey, UpdateApplicationField } from '../../types'
import { deriveAddressComponents, normalizeEmail } from '../../utils'
import {
  persistApplicationDraft,
  persistSelectedAddress,
  resetApplicationDraftForEmail,
  type UpdateEnrollmentDraft
} from '../../utils/draftPersistence'

type UseApplicationFormDraftActionsArgs = {
  clearFieldError: (field: FieldErrorKey) => void
  state: ApplicationFormState
  updateEnrollmentDraft: UpdateEnrollmentDraft
  updateField: UpdateApplicationField
}

export function useApplicationFormDraftActions({
  clearFieldError,
  state,
  updateEnrollmentDraft,
  updateField
}: UseApplicationFormDraftActionsArgs) {
  const getAddressParts = useCallback(
    () =>
      deriveAddressComponents(state.formData.addressLine1, {
        city: state.formData.city,
        state: state.formData.state,
        zipCode: state.formData.zipCode
      }),
    [state.formData.addressLine1, state.formData.city, state.formData.state, state.formData.zipCode]
  )

  const handleEmailChange = useCallback(
    (value: string) => {
      const nextEmail = normalizeEmail(value)
      const currentEmail = normalizeEmail(state.formData.email)
      const storedEmail = normalizeEmail(getEnrollmentDraftEmail())

      if (nextEmail && storedEmail && nextEmail !== storedEmail && currentEmail === storedEmail) {
        state.setFormData(resetApplicationDraftForEmail(value))
        state.setFullAddress('')
        clearFieldError('email')
        return
      }

      updateField('email', value)
      clearFieldError('email')
    },
    [clearFieldError, state, updateField]
  )

  const handleAddressChange = useCallback(
    (value: string) => {
      state.setFullAddress(value)
      updateField('addressLine1', value)
    },
    [state, updateField]
  )

  const handleAddressSelect = useCallback(
    (parts: AddressParts) => {
      const nextAddressLine1 = parts.addressLine1 || state.formData.addressLine1

      state.setFormData(previous => ({
        ...previous,
        addressLine1: nextAddressLine1,
        city: parts.city || '',
        state: parts.state || '',
        zipCode: parts.zipCode || ''
      }))

      updateEnrollmentDraft({
        addressLine1: nextAddressLine1,
        city: parts.city || '',
        state: parts.state || '',
        zipCode: parts.zipCode || ''
      })

      persistSelectedAddress({
        addressLine1: nextAddressLine1,
        addressParts: parts,
        updateEnrollmentDraft
      })

      state.setFieldErrors(errors => ({
        ...errors,
        addressLine1: undefined,
        city: undefined,
        state: undefined,
        zipCode: undefined
      }))
    },
    [state, updateEnrollmentDraft]
  )

  return {
    getAddressParts,
    handleAddressChange,
    handleAddressSelect,
    handleEmailChange,
    persistApplicationDraft: useCallback(() => {
      const addressComponents = getAddressParts()

      persistApplicationDraft({
        addressParts: {
          city: addressComponents.city,
          state: addressComponents.state,
          zipCode: addressComponents.zipCode
        },
        formData: state.formData,
        updateEnrollmentDraft
      })
    }, [getAddressParts, state.formData, updateEnrollmentDraft])
  }
}
